import { connect } from 'react-redux';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';

import { openGenericModal } from '@actions/modal';

import { Button } from '@commons/utils/styledLibraryComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';

import { getClientInfo } from '@selectors/client';

import { buildOrderHeaderSelection } from '../../utils/buildOrderHeaderSelection';
import { buildPreparationOrderProductLines } from '../../utils/buildPreparationOrderProductLines';
import { buildProductSearchMatcher } from '../../utils/buildProductSearchMatcher';
import { exportExternalOrder } from '../../utils/export';
import {
  getLeaveOrderFormModal,
  getRevertToInPreparationModal,
  getRevertToToComeModal,
} from '../../utils/getExternalOrderModals';
import { getPostDraftExternalOrderActions } from '../../utils/getPostDraftExternalOrderActions';
import { groupProductLinesByCategory } from '../../utils/groupProductLinesByCategory';
import { PREPARED_EXTERNAL_ORDER_STATUSES } from '../../constants';
import { useCollapsedCategories } from '../../hooks/useCollapsedCategories';
import { useOrderDeliveryEdition } from '../../hooks/useOrderDeliveryEdition';
import { useOrderPendingComment } from '../../hooks/useOrderPendingComment';
import OrderClientPanel from '../OrderClientPanel';
import OrderHeaderSelectors from '../OrderHeaderSelectors';
import OrderTableHeader from '../OrderTableHeader';
import OrderTopBar from '../OrderTopBar';

import { Content, FormContainer, SelectorsBar, TableCard } from '../orderFormStyledComponents';

import OrderPreparationProductTable from './components/OrderPreparationProductTable';

/** The BDL quantities and ticks of the order lines, as the table edits them. */
const buildInitialPreparedState = (productLines) =>
  productLines.reduce((preparedState, { productId, preparedQuantity, checked }) => {
    preparedState[productId] = { quantity: preparedQuantity, checked };

    return preparedState;
  }, {});

/**
 * The BDL lines to save, or `undefined` while every quantity and tick still matches the persisted
 * ones — saving what did not move would pointlessly regenerate the stock events of the order. An
 * input emptied on screen is saved as a 0: nothing was delivered for that line, and a line the
 * table never edited is sent as it was persisted.
 */
const buildPreparedQuantitiesPayload = (productLines, preparedStateByProductId) => {
  const hasPendingChange = productLines.some(({ productId, preparedQuantity, checked }) => {
    const preparedState = preparedStateByProductId[productId];

    if (!preparedState) {
      return false;
    }

    return (preparedState.quantity ?? 0) !== preparedQuantity || preparedState.checked !== checked;
  });

  if (!hasPendingChange) {
    return undefined;
  }

  return productLines.map(({ productId, preparedQuantity, checked }) => {
    const preparedState = preparedStateByProductId[productId];

    if (!preparedState) {
      return { productId, quantity: preparedQuantity, checked };
    }

    return { productId, quantity: preparedState.quantity ?? 0, checked: preparedState.checked };
  });
};

/**
 * Order at the `En préparation` and `Préparée` statuses: the BDC lines are frozen, and the table
 * displays the BDL quantities next to them. While the order is in preparation, the edit right opens
 * the BDL quantities, their preparer ticks and the comment — everything else already reads a
 * prepared order: same table, but the BDL turned into plain text and every control read-only.
 *
 * The ticks never save anything on their own: the page save button writes the quantities and the
 * ticks in one go, and the stock events of the order follow. The saves and the transitions from or
 * to `Préparée` close the order and land back on the list; cancelling the preparation stays on the
 * form (COR-1830), which reopens on the `A venir` view. A pending comment travels with the status
 * transitions rather than being dropped on the way — pending quantities travel the same way when
 * the order is marked prepared, and are deliberately dropped when the preparation is cancelled.
 */
export const PreparationExternalOrderForm = (props) => {
  const {
    externalOrder,
    canEditToComeAndInPreparation,
    canRevertToInPreparation,
    isSaving,
    onSavePreparation,
    onSaveDelivery,
    onMarkPrepared,
    onRevertToToCome,
    onRevertToInPreparation,
    onDownloadDeliveryNote,
    onClose,
    // From Redux
    stores,
    storeName,
    channels,
    hasMultipleChannels,
    currency,
    openModal,
  } = props;

  const isPrepared = PREPARED_EXTERNAL_ORDER_STATUSES.includes(externalOrder.status);

  // A prepared order is read-only whatever the rights: only its delivery still lets go of (COR-1771).
  const isEditAllowed = !isPrepared && canEditToComeAndInPreparation;

  const productLines = useMemo(
    () => buildPreparationOrderProductLines(externalOrder.products),
    [externalOrder],
  );

  const { comment, setComment, hasCommentChange, getPendingComment } =
    useOrderPendingComment(externalOrder);

  const {
    isEditingDelivery,
    editedDeliveryDate,
    editedDeliveryTime,
    deliveryPayload,
    hasDeliveryChange,
    isMissingDeliveryDate,
    getPendingDelivery,
    startDeliveryEdition,
    stopDeliveryEdition,
    handleChangeDeliveryDate,
    handleChangeDeliveryTime,
  } = useOrderDeliveryEdition(externalOrder);

  const [search, setSearch] = useState('');
  const [preparedStateByProductId, setPreparedStateByProductId] = useState(() =>
    buildInitialPreparedState(productLines),
  );

  const pendingProducts = useMemo(
    () => buildPreparedQuantitiesPayload(productLines, preparedStateByProductId),
    [productLines, preparedStateByProductId],
  );

  const hasUnsavedChanges = hasCommentChange || pendingProducts !== undefined || hasDeliveryChange;

  const translatedStoreName = getClientStoreNameTranslation(storeName);

  const {
    selectedStore,
    selectableStores,
    isChannelDisplayed,
    selectableChannels,
    selectedChannel,
    deliveryDate,
    deliveryTime,
    externalClient,
    selectableExternalClients,
  } = useMemo(
    () => buildOrderHeaderSelection(externalOrder, { stores, channels, hasMultipleChannels }),
    [externalOrder, stores, channels, hasMultipleChannels],
  );

  /**
   * The lines as they stand on screen, the edited BDL quantities and ticks overlaid on the
   * persisted ones. The table and the BDL total both read this single overlay, so an input emptied
   * to `null` weighs 0 in the metric exactly as it does on its line — and as a save would persist.
   */
  const statefulProductLines = useMemo(
    () =>
      productLines.map((productLine) => {
        const preparedState = preparedStateByProductId[productLine.productId];

        if (!preparedState) {
          return productLine;
        }

        return {
          ...productLine,
          preparedQuantity: preparedState.quantity,
          checked: preparedState.checked,
        };
      }),
    [productLines, preparedStateByProductId],
  );

  // The search narrows the table only: the line count and the totals describe the whole order.
  const displayedProductLines = useMemo(
    () => statefulProductLines.filter(buildProductSearchMatcher(search)),
    [statefulProductLines, search],
  );

  const categoryGroups = useMemo(
    () => groupProductLinesByCategory(displayedProductLines),
    [displayedProductLines],
  );

  const { collapsedCategories, toggleCategory } = useCollapsedCategories();

  const totalWithTaxes = useMemo(
    () =>
      productLines.reduce(
        (total, { quantity, priceWithTaxes }) => total + quantity * priceWithTaxes,
        0,
      ),
    [productLines],
  );

  const preparedTotalWithTaxes = useMemo(
    () =>
      statefulProductLines.reduce(
        (total, { preparedQuantity, priceWithTaxes }) =>
          total + (preparedQuantity ?? 0) * priceWithTaxes,
        0,
      ),
    [statefulProductLines],
  );

  const handleQuantityChange = useCallback((productId, quantity) => {
    setPreparedStateByProductId((preparedState) => ({
      ...preparedState,
      [productId]: { ...preparedState[productId], quantity },
    }));
  }, []);

  const handleToggleChecked = useCallback((productId) => {
    setPreparedStateByProductId((preparedState) => ({
      ...preparedState,
      [productId]: {
        ...preparedState[productId],
        checked: !preparedState[productId]?.checked,
      },
    }));
  }, []);

  const handleClose = () => {
    if (!hasUnsavedChanges) {
      onClose();

      return;
    }

    openModal(getLeaveOrderFormModal({ onConfirm: onClose }));
  };

  /**
   * A moved delivery closes the page through its dedicated route, the pending comment and BDL
   * quantities travelling with it. An untouched edition is simply dropped, the preparation save
   * keeping its own way out — and never firing when nothing else moved.
   */
  const handleSave = () => {
    const pendingComment = getPendingComment();

    if (isEditingDelivery && hasDeliveryChange) {
      onSaveDelivery({ delivery: deliveryPayload, pendingComment, products: pendingProducts });

      return;
    }

    if (isEditingDelivery) {
      stopDeliveryEdition();
    }

    if (pendingProducts === undefined && pendingComment === undefined) {
      return;
    }

    onSavePreparation({ products: pendingProducts, pendingComment });
  };

  // Deliberately unconfirmed (COR-1832): the transition is reversible, a modal only slows it down.
  const handleMarkPrepared = () => {
    onMarkPrepared({
      products: pendingProducts,
      pendingComment: getPendingComment(),
      delivery: getPendingDelivery(),
    });
  };

  // Cancelling the preparation drops the BDL: the pending quantities deliberately stay behind.
  const handleRevertToToCome = useCallback(() => {
    openModal(
      getRevertToToComeModal({
        onConfirm: () => {
          onRevertToToCome(getPendingComment());
        },
      }),
    );
  }, [openModal, onRevertToToCome, getPendingComment]);

  const handleRevertToInPreparation = useCallback(() => {
    openModal(
      getRevertToInPreparationModal({
        onConfirm: () => {
          onRevertToInPreparation();
        },
      }),
    );
  }, [openModal, onRevertToInPreparation]);

  /**
   * Export the order with its BDL columns (COR-1687), on the lines as they stand on screen —
   * pending quantities and all, since they are what the user sees. The search only ever narrows
   * the table: the export always carries the whole order.
   */
  const handleExport = useCallback(() => {
    exportExternalOrder({
      reference: externalOrder.reference,
      clientStoreName: storeName,
      storeName: selectedStore?.name,
      externalClientName: externalClient?.name,
      externalClientReference: externalClient?.externalReference,
      deliveryDate,
      createdAt: externalOrder.createdAt,
      productLines: statefulProductLines,
      hasPreparedQuantities: true,
      currency,
    });
  }, [
    externalOrder,
    storeName,
    selectedStore,
    externalClient,
    deliveryDate,
    statefulProductLines,
    currency,
  ]);

  /**
   * The backward transition of the status, when its right allows it: `A venir` reopens an order in
   * preparation, `En préparation` a prepared one. Without it the export stands alone as an icon
   * button.
   */
  const revertAction = useMemo(() => {
    if (isPrepared) {
      if (!canRevertToInPreparation) {
        return null;
      }

      return {
        id: 'action-revert-order-to-in-preparation',
        label: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_IN_PREPARATION'),
        handleClick: handleRevertToInPreparation,
      };
    }

    if (!canEditToComeAndInPreparation) {
      return null;
    }

    return {
      id: 'action-revert-order-to-to-come',
      label: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_TO_COME'),
      handleClick: handleRevertToToCome,
    };
  }, [
    isPrepared,
    canRevertToInPreparation,
    canEditToComeAndInPreparation,
    handleRevertToInPreparation,
    handleRevertToToCome,
  ]);

  const actions = useMemo(
    () =>
      getPostDraftExternalOrderActions({
        revertAction,
        onEditDelivery: canEditToComeAndInPreparation ? startDeliveryEdition : null,
        onExport: handleExport,
        // Gated by no right, but only a prepared order carries a BDL worth a delivery note
        onExportDeliveryNote: isPrepared ? onDownloadDeliveryNote : null,
      }),
    [
      revertAction,
      canEditToComeAndInPreparation,
      startDeliveryEdition,
      handleExport,
      isPrepared,
      onDownloadDeliveryNote,
    ],
  );

  // The export always stands in the menu: it only collapses into an icon button when alone.
  const hasActionsMenu = actions.length > 1;

  return (
    <FormContainer>
      <OrderTopBar
        externalOrderId={externalOrder.id}
        reference={externalOrder.reference}
        status={externalOrder.status}
        updatedAt={externalOrder.updatedAt}
        isSaved
      >
        <Button
          color={'inpulse-grey'}
          handleClick={handleClose}
          icon={'/images/inpulse/close-white-small.svg'}
        />
        {/* The delivery edition brings its own save on a prepared order, otherwise read-only. */}
        {(isEditAllowed || isEditingDelivery) && (
          <Button
            color={'inpulse-default'}
            handleClick={handleSave}
            icon={'/images/inpulse/save-white-small.svg'}
            isDisabled={!hasUnsavedChanges || isMissingDeliveryDate || isSaving}
          />
        )}
        {isEditAllowed && (
          <Button
            color={'inpulse-default'}
            handleClick={handleMarkPrepared}
            icon={'/images/inpulse/check-white-small.svg'}
            isDisabled={isSaving || isMissingDeliveryDate}
            label={i18next.t('EXTERNAL_ORDERS.FORM.MARK_PREPARED')}
          />
        )}
      </OrderTopBar>

      <SelectorsBar>
        <OrderHeaderSelectors
          channels={selectableChannels}
          deliveryDate={isEditingDelivery ? editedDeliveryDate : deliveryDate}
          deliveryTime={isEditingDelivery ? editedDeliveryTime : deliveryTime}
          externalClients={selectableExternalClients}
          isChannelDisplayed={isChannelDisplayed}
          isDeliveryEditable={isEditingDelivery}
          selectedChannel={selectedChannel}
          selectedExternalClient={externalClient}
          selectedStore={selectedStore}
          storeName={translatedStoreName}
          stores={selectableStores}
          isReadOnly
          onChangeDeliveryDate={handleChangeDeliveryDate}
          onChangeDeliveryTime={handleChangeDeliveryTime}
        />
        {/* Without any gated action left, the export stands alone as an icon button. */}
        {hasActionsMenu && (
          <Button
            actions={actions}
            color={'inpulse-outline'}
            iconDropdown={'/images/inpulse/ellipsis-black-small.svg'}
          />
        )}
        {!hasActionsMenu && (
          <Button
            color={'inpulse-outline'}
            handleClick={handleExport}
            icon={'/images/inpulse/file-download-black-small.svg'}
          />
        )}
      </SelectorsBar>

      <Content>
        <TableCard>
          <OrderTableHeader
            lineCount={productLines.length}
            preparedTotalWithTaxes={preparedTotalWithTaxes}
            search={search}
            totalWithTaxes={totalWithTaxes}
            onSearchChange={setSearch}
          />
          <OrderPreparationProductTable
            categoryGroups={categoryGroups}
            collapsedCategories={collapsedCategories}
            hasSearch={!!search}
            isEditAllowed={isEditAllowed}
            isPrepared={isPrepared}
            onQuantityChange={handleQuantityChange}
            onToggleCategory={toggleCategory}
            onToggleChecked={handleToggleChecked}
          />
        </TableCard>
        <OrderClientPanel
          comment={comment}
          externalClient={externalClient}
          isCommentReadOnly={!isEditAllowed}
          onChangeComment={setComment}
        />
      </Content>
    </FormContainer>
  );
};

PreparationExternalOrderForm.propTypes = {
  externalOrder: PropTypes.shape({
    id: PropTypes.string,
    reference: PropTypes.string,
    status: PropTypes.string,
    storeId: PropTypes.string,
    store: PropTypes.object,
    externalClient: PropTypes.object,
    channelId: PropTypes.string,
    deliveryDate: PropTypes.string,
    deliveryTime: PropTypes.string,
    comment: PropTypes.string,
    updatedAt: PropTypes.string,
    products: PropTypes.array,
  }).isRequired,
  canEditToComeAndInPreparation: PropTypes.bool.isRequired,
  canRevertToInPreparation: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onSavePreparation: PropTypes.func.isRequired,
  onSaveDelivery: PropTypes.func.isRequired,
  onMarkPrepared: PropTypes.func.isRequired,
  onRevertToToCome: PropTypes.func.isRequired,
  onRevertToInPreparation: PropTypes.func.isRequired,
  onDownloadDeliveryNote: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const { storeName, clientChannels, hasMultipleChannels } = getClientInfo(state.baseReducer.user);

  return {
    stores: state.baseReducer.activeStores,
    storeName,
    channels: clientChannels,
    hasMultipleChannels,
    currency: state.baseReducer.currency,
  };
};

const mapDispatchToProps = (dispatch) => ({
  openModal: (params) => {
    dispatch(openGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PreparationExternalOrderForm);
